import { useTheme } from "../../../context/ThemeContext";
import { FiCheck, FiX } from "react-icons/fi";

/**
 * LayoutPresetSelector
 * ========================================
 * Visual layout picker dropdown
 * Mobile-friendly with responsive positioning
 */
export default function LayoutPresetSelector({ currentLayout, onSelectLayout, onClose }) {
  const { theme } = useTheme();

  const layouts = [
    {
      id: "spotlight",
      name: "Spotlight",
      description: "1 large + 7 small",
      icon: <SpotlightIcon />,
    },
    {
      id: "grid-4",
      name: "2×2 Grid",
      description: "4 cameras",
      icon: <GridIcon cols={2} />,
    },
    {
      id: "grid-9",
      name: "3×3 Grid",
      description: "9 cameras",
      icon: <GridIcon cols={3} />,
    },
    {
      id: "grid-16",
      name: "4×4 Grid",
      description: "16 cameras",
      icon: <GridIcon cols={4} />,
    },
    {
      id: "grid-36",
      name: "6×6 Grid",
      description: "36 cameras",
      icon: <GridIcon cols={6} />,
    },
    {
      id: "table",
      name: "Table View",
      description: "Detailed table",
      icon: <TableIcon />,
    },
    {
      id: "list",
      name: "List View",
      description: "Vertical list",
      icon: <ListIcon />,
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />

      {/* Dropdown - Mobile Friendly */}
      <div className={`
        fixed md:absolute 
        inset-x-4 bottom-4 md:inset-x-auto md:bottom-auto
        md:top-full md:right-0 md:mt-2
        w-auto md:w-80
        rounded-xl shadow-2xl border z-50 overflow-hidden
        ${theme === "dark"
          ? "bg-neutral-800 border-neutral-700"
          : "bg-white border-gray-200"
        }
      `}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center justify-between ${
          theme === "dark" ? "border-neutral-700" : "border-gray-200"
        }`}>
          <div>
            <h3 className={`font-semibold ${
              theme === "dark" ? "text-gray-100" : "text-gray-900"
            }`}>
              Select Layout
            </h3>
            <p className={`text-xs mt-1 ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            }`}>
              Choose how to display cameras
            </p>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className={`md:hidden p-2 rounded-lg transition ${
              theme === "dark"
                ? "hover:bg-neutral-700 text-gray-400"
                : "hover:bg-gray-100 text-gray-600"
            }`}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Layout Grid */}
        <div className="p-2 max-h-[60vh] md:max-h-96 overflow-y-auto">
          {layouts.map(layout => (
            <button
              key={layout.id}
              onClick={() => onSelectLayout(layout.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                currentLayout === layout.id
                  ? theme === "dark"
                    ? "bg-blue-600 text-white"
                    : "bg-blue-600 text-white"
                  : theme === "dark"
                    ? "hover:bg-neutral-700 text-gray-200"
                    : "hover:bg-gray-50 text-gray-900"
              }`}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                currentLayout === layout.id
                  ? "bg-white/20"
                  : theme === "dark"
                    ? "bg-neutral-700"
                    : "bg-gray-100"
              }`}>
                {layout.icon}
              </div>

              {/* Info */}
              <div className="flex-1 text-left">
                <div className="font-semibold text-sm">{layout.name}</div>
                <div className={`text-xs ${
                  currentLayout === layout.id
                    ? "text-white/80"
                    : theme === "dark"
                      ? "text-gray-400"
                      : "text-gray-500"
                }`}>
                  {layout.description}
                </div>
              </div>

              {/* Check Mark */}
              {currentLayout === layout.id && (
                <FiCheck size={20} className="flex-shrink-0" />
              )}
            </button>
          ))}
        </div>

        {/* Footer Tip */}
        <div className={`px-4 py-3 border-t text-xs ${
          theme === "dark"
            ? "border-neutral-700 text-gray-400"
            : "border-gray-200 text-gray-500"
        }`}>
          💡 <span className="font-medium">Tip:</span> Press 1-4 for quick layout switching
        </div>
      </div>
    </>
  );
}

/* ========================================
   Layout Icons
======================================== */

function SpotlightIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-current">
      <rect x="2" y="2" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
      <rect x="22" y="2" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="22" y="8" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="22" y="14" width="8" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function GridIcon({ cols }) {
  const gap = 2;
  const size = (32 - (cols + 1) * gap) / cols;
  
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-current">
      {Array.from({ length: cols * cols }).map((_, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const x = gap + col * (size + gap);
        const y = gap + row * (size + gap);
        
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={size}
            height={size}
            rx="1"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        );
      })}
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-current">
      <rect x="2" y="3" width="28" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="2" y="13" width="28" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
      <rect x="2" y="23" width="28" height="6" rx="1" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-current">
      {/* Header row */}
      <rect x="2" y="2" width="28" height="6" rx="1" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
      {/* Rows */}
      <line x1="2" y1="8" x2="30" y2="8" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="14" x2="30" y2="14" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="20" x2="30" y2="20" stroke="currentColor" strokeWidth="1.5" />
      <line x1="2" y1="26" x2="30" y2="26" stroke="currentColor" strokeWidth="1.5" />
      {/* Columns */}
      <line x1="10" y1="2" x2="10" y2="30" stroke="currentColor" strokeWidth="1.5" />
      <line x1="18" y1="2" x2="18" y2="30" stroke="currentColor" strokeWidth="1.5" />
      <line x1="26" y1="2" x2="26" y2="30" stroke="currentColor" strokeWidth="1.5" />
      {/* Border */}
      <rect x="2" y="2" width="28" height="28" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
    </svg>
  );
}
